import { computed, onMounted, onBeforeUnmount, watch } from 'vue';
import WorkforceAPI from 'dashboard/api/workforce';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';

let activityTrackerInstance = null;
let debounceTimer = null;
let lastActivityTime = null;
const DEBOUNCE_DELAY = 5000; // 5秒防抖，避免过于频繁的API调用
const MIN_ACTIVITY_INTERVAL = 10000; // 最小活动间隔10秒，避免重复发送

class ActivityTracker {
  constructor() {
    this.isActive = false;
    this.eventHandlers = {
      click: this.handleActivity.bind(this),
      keydown: this.handleActivity.bind(this),
      mousemove: this.handleActivity.bind(this),
      scroll: this.handleActivity.bind(this),
      touchstart: this.handleActivity.bind(this),
    };
  }

  handleActivity() {
    if (!this.isActive) return;

    const now = Date.now();

    // 如果距离上次发送不到最小间隔，跳过
    if (lastActivityTime && now - lastActivityTime < MIN_ACTIVITY_INTERVAL) {
      return;
    }

    // 清除之前的防抖定时器
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // 设置新的防抖定时器
    debounceTimer = setTimeout(() => {
      this.sendActivity();
    }, DEBOUNCE_DELAY);
  }

  // eslint-disable-next-line class-methods-use-this
  async sendActivity() {
    try {
      await WorkforceAPI.trackActivity();
      lastActivityTime = Date.now();
    } catch (error) {
      // 静默失败，不影响用户体验
      console.debug('[ActivityTracker] Failed to track activity:', error);
    }
  }

  start() {
    if (this.isActive) return;

    this.isActive = true;

    // 添加全局事件监听器
    Object.entries(this.eventHandlers).forEach(([event, handler]) => {
      document.addEventListener(event, handler, { passive: true });
    });

    // 立即发送一次活动（用户已在使用应用）
    this.sendActivity();
  }

  stop() {
    if (!this.isActive) return;

    this.isActive = false;

    // 移除所有事件监听器
    Object.entries(this.eventHandlers).forEach(([event, handler]) => {
      document.removeEventListener(event, handler);
    });

    // 清除防抖定时器
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  }
}

export function useActivityTracker() {
  // 单例模式，确保全局只有一个追踪器
  if (!activityTrackerInstance) {
    activityTrackerInstance = new ActivityTracker();
  }

  const { accountId } = useAccount();
  const isFeatureEnabledonAccount = useMapGetter(
    'accounts/isFeatureEnabledonAccount'
  );

  const isWorkforceEnabled = computed(() => {
    const id = accountId.value;
    if (!Number.isFinite(id) || id <= 0) return false;
    return isFeatureEnabledonAccount.value(
      id,
      FEATURE_FLAGS.WORKFORCE_MANAGEMENT
    );
  });

  const syncTrackerState = enabled => {
    if (enabled) activityTrackerInstance.start();
    else activityTrackerInstance.stop();
  };

  onMounted(() => {
    syncTrackerState(isWorkforceEnabled.value);
  });

  watch(isWorkforceEnabled, enabled => {
    syncTrackerState(enabled);
  });

  onBeforeUnmount(() => {
    // 注意：不要在这里stop，因为这是全局追踪器
    // 如果组件卸载就停止，会影响其他组件
  });

  return {
    start: () => activityTrackerInstance.start(),
    stop: () => activityTrackerInstance.stop(),
  };
}

// 导出单例，供全局使用
export default activityTrackerInstance;
